import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTransition, animated, useSpring } from "@react-spring/web";
import cx from "classnames";
import styles from "./manageSource.module.scss";
import Slider from "../slider";
import Button from "../button";
import {
  deleteDataSourceMapping,
  fetchDataSource,
  fetchDataSourceMappings,
  invalidateDataSourceDeletion,
  invalidateDataSourceSync,
  syncDataSourceMapping,
} from "../../redux/actions/dataSource";
import { StreamDetail } from "../../pages/streams/types";
import dateFormat from "dateformat";
import { Col, Row } from "react-bootstrap";
import _ from "lodash";
import Mappings from "./mappings";
import EmptyMessage from "../emptyMessage";
import Tooltip from "../tooltip";
import { NavLink, useParams } from "react-router-dom";
import { fetchStreamChildren } from "src/redux/actions/streams";
import { fetchGroupChildren } from "src/redux/actions/groups";
import { toast } from "react-toastify";
import Toast from "../toast";
import { useStreamsAdoption } from "src/redux/actions/adoption/streams";

interface ManageSourceProps {
  openManageSource: boolean;
  entityType: string;
  stream?: StreamDetail;
  group?: any; //group detail
  handleClose: () => void;
}

const ManageSource: React.FC<ManageSourceProps> = ({
  openManageSource,
  entityType,
  stream,
  handleClose,
  group,
}) => {
  const dispatch = useDispatch();
  const { trackMapNewFolderClicked, trackManageSourceScreenView } =
    useStreamsAdoption();
  const { id } = useParams<{ id: string }>();

  const loadingMappings = useSelector((state: any) => state.dataSource.loading);
  const existingMappings = useSelector(
    (state: any) => state.dataSource.mappings.items,
  );
  const loadingExistingMappings = useSelector(
    (state: any) => state.dataSource.mappings.loading,
  );
  const syncStatus = useSelector((state: any) => state.dataSource.syncProgress);
  const loadingStreamChildren = useSelector(
    (state: any) => _.get(state, `stream[${id}].loadingChildren`) || false,
  );
  const loadingGroupChildren = useSelector((state: any) =>
    _.get(state, `group[${id}].loadingChildren`, null),
  );
  const deletedMappings = useSelector(
    (state: any) => state.dataSource.deletion,
  );

  const [createNewMapping, setCreateNewMapping] = useState(false);
  const [canRenderMappings, setCanRenderMappings] = useState(false);
  const [isloadingMappings, setIsLoadingMappings] = useState(false);

  const transitions = useTransition(!createNewMapping, {
    from: { transform: "translateX(-100%)", opacity: 1 },
    enter: { transform: "translateX(0%)", opacity: 1 },
    leave: { transform: "translateX(-100%)", opacity: 0 },
    onRest: () => {
      if (createNewMapping) {
        setCanRenderMappings(true);
      }
    },
  });

  useEffect(() => {
    dispatch(fetchDataSource(""));
    dispatch(fetchDataSourceMappings({ entity: stream || group }));
    trackManageSourceScreenView();
  }, []);

  useEffect(() => {}, [syncStatus]);

  useEffect(() => {
    if (_.size(deletedMappings) > 0) {
      toast(<Toast.Sucess message={"Successfully deleted mapping!"} />, {
        autoClose: 3000,
      });

      dispatch(invalidateDataSourceDeletion());
    }
  }, [deletedMappings]);

  const handleSyncMappingClicked = (mapping: any) => {
    dispatch(syncDataSourceMapping({ id: mapping.id }));
  };

  const handleDeleteMappingClicked = (mapping: any) => {
    dispatch(deleteDataSourceMapping({ id: mapping.id }));
  };

  const handleFetchParentChildren = () => {
    if (stream && !loadingStreamChildren) {
      dispatch(
        fetchStreamChildren({
          id: id || "",
          page: 0,
          limit: 25,
          sort: { order: "DESC", type: "name" },
        }),
      );
      dispatch(invalidateDataSourceSync());
      handleClose();
    }

    if (group && !loadingGroupChildren) {
      dispatch(
        fetchGroupChildren({
          id: id || "",
          page: 0,
          limit: 25,
          sort: { order: "ASC", type: "name" },
        }),
      );
      dispatch(invalidateDataSourceSync());
      handleClose();
    }
  };

  const renderExistingMappings = () => {
    return transitions((style, item) =>
      item ? (
        <animated.div style={style}>
          <span
            className={styles.mapButton}
            onClick={() => setCreateNewMapping(true)}
          >
            <i className="paperflite-add"></i> Map a new folder
          </span>
          <div className={styles.existingMappings}>
            {existingMappings.map((mapping: any) => {
              const isSyncing =
                mapping.status === "IN_PROGRESS" ||
                mapping.status === "SYNC_REQUESTED";
              const mappingSyncStatus =
                syncStatus &&
                syncStatus.find((status: any) => status.id === mapping.id);
              const isDeleting = existingMappings.find(
                (m: any) => m.id === mapping.id,
              ).deleting;

              if (mappingSyncStatus && mappingSyncStatus.completed) {
                handleFetchParentChildren();
              }

              return (
                <div key={mapping.id} className={styles.existingMapping}>
                  <Row className={styles.details}>
                    <Col
                      md={1}
                      className={cx(
                        styles.mappingIcon,
                        styles[mapping.dataSource.storageType],
                      )}
                    ></Col>
                    <Col md={9} className={styles.storageName}>
                      {mapping.dataSource.name}&apos;s{" "}
                      {mapping.dataSource.storageName}
                    </Col>
                    <Col
                      md={1}
                      className={cx("paperflite-reverse-curved", styles.icon, {
                        [styles.syncing]: isSyncing,
                        [styles.disabled]:
                          _.get(mapping, "dataSource.migrationStatus") ===
                          "AUTH_ERROR",
                      })}
                      onClick={() => handleSyncMappingClicked(mapping)}
                    ></Col>
                    <Col
                      md={1}
                      className={cx(
                        "paperflite-delete",
                        styles.icon,
                        styles.deleteIcon,
                        {
                          [styles.disabled]: isSyncing || isDeleting,
                        },
                      )}
                      onClick={() => handleDeleteMappingClicked(mapping)}
                    ></Col>
                  </Row>
                  <Row>
                    <span className={styles.folderName}>{mapping.folder}</span>
                    <span className={styles.syncData}>
                      last sync:{" "}
                      {dateFormat(mapping.lastSyncDate, "dd mmm yyyy")}
                    </span>
                  </Row>
                  {(isSyncing || mappingSyncStatus) && (
                    <Row className={styles.syncWrapper}>
                      <span className={styles.syncStatus}>
                        {_.get(mappingSyncStatus, "completed")
                          ? "Synchronised!"
                          : "Synchronising..."}
                      </span>
                    </Row>
                  )}
                  {mappingSyncStatus && (
                    <Row className={styles.assetStatusWrapper}>
                      <Col md={4} className={styles.assetStatus}>
                        <span className={styles.assetCount}>
                          {_.get(mappingSyncStatus, "newAssets", 0)}
                        </span>
                        <span className={styles.assetLabel}>New Assets</span>
                      </Col>
                      <Col md={4} className={styles.assetStatus}>
                        <span className={styles.assetCount}>
                          {_.get(mappingSyncStatus, "existingAssets", 0)}
                        </span>
                        <span className={styles.assetLabel}>
                          Existing Assets
                        </span>
                      </Col>
                    </Row>
                  )}
                  {_.get(mapping, "dataSource.migrationStatus") ===
                    "AUTH_ERROR" && (
                    <Row className={styles.warning}>
                      <span className={styles.text}>
                        Your authentication token to{" "}
                        <span className={styles.storageName}>
                          {mapping.dataSource.name +
                            " " +
                            mapping.dataSource.storageName}{" "}
                        </span>{" "}
                        content source has expired. Please re-authenticate to
                        stay connected to your datasource
                        <NavLink
                          to="/settings/data-sources"
                          className={styles.link}
                        >
                          {" "}
                          Re-authenticate
                        </NavLink>
                      </span>
                    </Row>
                  )}
                  <span className={styles.rowBorder}></span>
                </div>
              );
            })}
          </div>
        </animated.div>
      ) : null,
    );
  };

  return (
    <Slider.Dialog
      className={styles.container}
      width="300"
      show={openManageSource}
      onClose={handleClose}
    >
      {!canRenderMappings &&
        transitions((style, item) =>
          item ? (
            <animated.div style={style} className={styles.header}>
              <Slider.Header title="manage content source">
                <i
                  className={cx(styles.closeIcon, "paperflite-close")}
                  onClick={handleClose}
                ></i>
                <p className={styles.subTitle}>
                  Your one-stop shop for managing all content sources
                </p>
              </Slider.Header>
            </animated.div>
          ) : null,
        )}

      <div className={styles.subContainer}>
        {transitions((style, item) =>
          item && !canRenderMappings && loadingExistingMappings ? (
            <animated.span style={style}>
              <div className={cx(styles.placeholder)} style={{ width: "70%" }}>
                <div className={cx(styles.mapping, styles.block)}></div>
              </div>
              <div className={cx(styles.placeholder)} style={{ width: "50%" }}>
                <div className={cx(styles.mapping, styles.block)}></div>
              </div>
            </animated.span>
          ) : null,
        )}

        {!canRenderMappings &&
          !loadingExistingMappings &&
          _.size(existingMappings) > 0 &&
          renderExistingMappings()}
          
          {isloadingMappings && (
          <div className={styles.loadingContainer}>
            <p>Synchronising...</p>
          </div>
        )}

        {!canRenderMappings &&
          !loadingExistingMappings &&
          _.size(existingMappings) === 0 &&
          !isloadingMappings &&
          transitions((style, item) =>
            item ? (
              <animated.span style={style}>
                <div className={styles.emptyContainer}>
                  <EmptyMessage
                    width="auto"
                    image={"/images/manageSources/empty.svg"}
                    heading="No content source found. Time to add some fresh sources!"
                    message=""
                    action={{
                      title: "Add new content source",
                      icon: "paperflite-add",
                      onClick: () => {
                        setCreateNewMapping(true);
                        trackMapNewFolderClicked({
                          streamId: stream?.id,
                          groupId: group?.id,
                        });
                      },
                    }}
                  />
                </div>
              </animated.span>
            ) : null,
          )}

        {canRenderMappings && loadingMappings && (
          <div className={styles.placeholderContainer}>
            <div className={cx(styles.placeholder)} style={{ width: "70%" }}>
              <div className={cx(styles.mapping, styles.block)}></div>
            </div>
            <div className={cx(styles.placeholder)} style={{ width: "50%" }}>
              <div className={cx(styles.mapping, styles.block)}></div>
            </div>
          </div>
        )}

        {canRenderMappings && !loadingMappings && (
          <Mappings
            setCanRenderMappings={setCanRenderMappings}
            setCreateNewMapping={setCreateNewMapping}
            entityType={entityType}
            stream={stream}
            group={group}
            handleClose={handleClose}
            setIsLoadingMappings={setIsLoadingMappings} 
          />
        )}
      </div>
    </Slider.Dialog>
  );
};

export default ManageSource;

interface ActionConfigProps {
  showConfirmAction: boolean;
  entityType: string;
  stream?: StreamDetail;
  group?: any;
  selectedFolder: any;
  handleConfirmClick: () => void;
  handleClose: () => void;
}
export const ActionConfig: React.FC<ActionConfigProps> = ({
  showConfirmAction,
  entityType,
  stream,
  group,
  selectedFolder,
  handleConfirmClick,
  handleClose,
}) => {
  const confirmActionTransition = useTransition(showConfirmAction, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 0 },
  });

  const entityName = entityType === "stream" ? stream?.name : group.name;

  return (
    <div>
      {confirmActionTransition((style, item) =>
        item ? (
          <animated.div
            style={{
              ...style,
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
            }}
          >
            <div className={styles.confirmAction}>
              <div className={styles.confirmDetails}>
                Map Files from <span>&quot;{selectedFolder.name}&quot;</span> to the{" "}
                {entityType}
                <Tooltip content={entityName} placement="top">
                  <span className={styles.entityName}>{entityName}</span>
                </Tooltip>
              </div>
              <Slider.Actions>
                <Button
                  icon="paperflite-tick"
                  text="Confirm"
                  variant="primary"
                  onClick={() => handleConfirmClick()}
                />
                <Button
                  text="Cancel"
                  variant="secondary"
                  onClick={handleClose}
                />
              </Slider.Actions>
            </div>
          </animated.div>
        ) : null,
      )}
    </div>
  );
};
